// Copyright 2025 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { Point2 } from "libraries/DataTypes/Point2";

export type PrpdBinData = {
    dischargeDataPoints: Point2[];
    pulseCount: number;
};

export interface IPrpdDataService {
    parsePrpdBin(arrayBuffer: ArrayBuffer): PrpdBinData;
}

@autoinject()
export class PrpdDataService implements IPrpdDataService {
    /**
     * Parses the content of a Prpd.bin file (expected to be a series of interleaved float32 phase and value pairs).
     * @param arrayBuffer The ArrayBuffer containing the binary data from Prppd.bin.
     * @returns An object containing the parsed dischargeDataPoints and the pulseCount.
     */
    public parsePrpdBin(arrayBuffer: ArrayBuffer): PrpdBinData {
        if (arrayBuffer.byteLength % 4 !== 0) {
            console.warn("Warning: partial discharge file size is not a list of 32-bit floats.");
            return { dischargeDataPoints: [], pulseCount: 0 };
        }

        const floatData = new Float32Array(arrayBuffer);
        const floatCount = floatData.length;

        if (floatCount % 2 !== 0) {
            console.warn(
                "Warning: The number of floats in the partial discharge file is odd. Expected interleaved phase/value pairs. Plotting what is available."
            );
        }

        if (floatCount === 0) {
            console.warn("The partial discharge file is empty or contains no float data.");
            return { dischargeDataPoints: [], pulseCount: 0 };
        }

        const dischargeDataPoints: Point2[] = [];

        for (let i = 0; i < floatCount; i += 2) {
            const phase = floatData[i];
            const value = i + 1 < floatCount ? floatData[i + 1] : NaN;

            if (!isNaN(phase) && typeof value === "number" && !isNaN(value)) {
                let normalizedPhase = phase;
                if (phase < 0) {
                    normalizedPhase = phase + 360; // Normalize negative phases to be within 0-360 range
                }
                dischargeDataPoints.push({ x: normalizedPhase, y: value });
            }
        }

        const pulseCount = dischargeDataPoints.length;

        return {
            dischargeDataPoints: dischargeDataPoints,
            pulseCount: pulseCount,
        };
    }
}
